@import '../../utils/control-density.css';
@import '../../utils/chrome-layout.css';
@import '../../utils/focus-ring.css';
@import '../../utils/interaction-fill.css';
@import '../../utils/scrollbar-hidden.css';

/* ── Theme variables ──────────────────────────────────────────────────────── */

.panel-nav {
  /* ─ Sizes ─ */
  --_nav-width:              var(--dimension-panel-width-2xs);
  --_nav-width-collapsed:    var(--dimension-size-600);
  --_nav-item-height:        var(--dimension-size-400, 32px);
  --_nav-padding-x:          var(--dimension-space-100, 8px);
  --_nav-padding-y:          var(--dimension-space-100, 8px);
  /* gear button outer width = padding*2 + icon */
  --_nav-footer-btn-w:       calc(var(--dimension-space-075, 6px) * 2 + var(--dimension-iconography-md, 20px));

  /* ─ Motion ─ */
  /* --ds-panel-nav-speed: unitless multiplier; override in Storybook to slow-mo (e.g. 10 = 10×).
     *-dur vars are duration-only (needed for calc); *-motion vars add easing for use in transitions. */
  --_nav-speed:               var(--ds-panel-nav-speed, 1);
  --_nav-expand-dur:          calc(var(--_nav-speed) * var(--effect-animation-duration-medium-1, 300ms));
  --_nav-expand-motion:       var(--_nav-expand-dur) var(--effect-animation-easing-ease-in-out, ease);
  --_nav-label-fade-dur:      calc(var(--_nav-speed) * var(--effect-animation-duration-medium-1, 300ms));
  --_nav-label-fade-motion:   var(--_nav-label-fade-dur) var(--effect-animation-easing-ease-in-out, ease);
  --_nav-label-fade-out-dur:  calc(var(--_nav-speed) * var(--effect-animation-duration-medium-1, 300ms));
  --_nav-label-fade-out-motion: var(--_nav-label-fade-out-dur) var(--effect-animation-easing-ease-in-out, ease);

  /* ─ Colours – default chrome (app surfaces) ─ */
  --_nav-bg:           var(--color-background-secondary);
  --_nav-border:       var(--color-border-tertiary);
  --_nav-divider:      var(--color-border-tertiary);
  --_nav-fg-primary:   var(--color-foreground-primary);
  --_nav-fg-secondary: var(--color-foreground-secondary);
  --_nav-fg-tertiary:  var(--color-foreground-tertiary);
  --_nav-hover:        var(--color-interaction-hover);
  --_nav-pressed:      var(--color-interaction-pressed);
  --_nav-focus:        var(--color-interaction-focus);
  --_nav-dot:          var(--color-foreground-bold-brand);
  --ds-interaction-hover: var(--_nav-hover);
  --ds-interaction-pressed: var(--_nav-pressed);
  --ds-focus-ring-color: var(--_nav-focus);
}

/* ── Default chrome overrides ────────────────────────────────────────────── */

/* Shell-owned chrome (bg + wash) — panel is a transparent frame. */
:host-context(ds-shell-app:not([gradient-preset='none'])) .panel-nav,
:host-context(ds-shell-app.shell-app--paper-texture) .panel-nav {
  background-color: transparent;
}

/* Style slots — same app tokens for now; hook texture/glyph layers on these classes later. */
.panel-nav--dashboard,
.panel-nav--settings {
  --_nav-bg:           var(--color-background-secondary);
  --_nav-border:       var(--color-border-tertiary);
  --_nav-divider:      var(--color-border-tertiary);
  --_nav-fg-primary:   var(--color-foreground-primary);
  --_nav-fg-secondary: var(--color-foreground-secondary);
  --_nav-fg-tertiary:  var(--color-foreground-tertiary);
  --_nav-hover:        var(--color-interaction-hover);
  --_nav-pressed:      var(--color-interaction-pressed);
  --_nav-focus:        var(--color-interaction-focus);
  --_nav-dot:          var(--color-foreground-bold-brand);
  --ds-interaction-hover: var(--_nav-hover);
  --ds-interaction-pressed: var(--_nav-pressed);
  --ds-focus-ring-color: var(--_nav-focus);
}

/* ── Shell ───────────────────────────────────────────────────────────────── */

.panel-nav {
  display: flex;
  flex-direction: column;
  position: relative;
  width: var(--_nav-width);
  min-width: var(--_nav-width);
  height: 100%;
  background-color: var(--_nav-bg);
  border-right: var(--dimension-stroke-width-012, 1px) solid var(--_nav-border);
  box-sizing: border-box;
  overflow: visible;
  transition:
    width var(--_nav-expand-motion),
    min-width var(--_nav-expand-motion),
    background-color var(--effect-motion-short-3, 200ms ease),
    border-color var(--effect-motion-short-3, 200ms ease);
  font-family: var(--typography-font-family-ui, system-ui);
  isolation: isolate;
}

.panel-nav__header,
.panel-nav__body,
.panel-nav__footer {
  position: relative;
  z-index: 3;
}

.panel-nav--collapsed {
  width: var(--_nav-width-collapsed);
  min-width: var(--_nav-width-collapsed);
}

/* ── Header ──────────────────────────────────────────────────────────────── */

.panel-nav__header {
  height: var(--dimension-size-600, 48px);
  flex-shrink: 0;
  border-bottom: var(--dimension-stroke-width-012, 1px) solid var(--_nav-divider);
  transition: border-color var(--effect-motion-short-3, 200ms ease);
}

.panel-nav__header-btn {
  all: unset;
  /* Re-assert after unset — required for `.ds-interaction-fill` stacking. */
  position: relative;
  z-index: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--dimension-space-075, 6px);
  border-radius: var(--ds-control-radius, var(--dimension-radius-025, 2px));
  cursor: default;
  color: var(--_nav-fg-primary);
  flex-shrink: 0;
  box-sizing: border-box;
  transition: color var(--effect-motion-short-3, 200ms ease);
  /* Hover/press: `.ds-interaction-fill` (tokens from --_nav-* via --ds-interaction-*). */
}

.panel-nav--breakpoint-locked .panel-nav__header-btn {
  cursor: default;
}

/* Logo — visible by default; instant swap to toggle on hover (no crossfade overlap) */
.panel-nav__header-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  opacity: 1;
}

.panel-nav__m-mark {
  width: var(--dimension-iconography-md, 20px);
  height: var(--dimension-iconography-md, 20px);
  display: block;
}

/* Toggle icon — stacked on logo; under hover wash (::after z-index 3) */
.panel-nav__header-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  inset: 0;
  z-index: 2;
  opacity: 0;
  pointer-events: none;
}

@media (hover: hover) and (pointer: fine) {
  .panel-nav:not(.panel-nav--breakpoint-locked)
    .panel-nav__header-btn:hover
    .panel-nav__header-logo {
    opacity: 0;
  }

  .panel-nav:not(.panel-nav--breakpoint-locked)
    .panel-nav__header-btn:hover
    .panel-nav__header-toggle {
    opacity: 1;
  }
}

/* ── Scrollable body ─────────────────────────────────────────────────────── */

.panel-nav__body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.panel-nav__sections {
  display: flex;
  flex-direction: column;
  gap: var(--dimension-space-200, 16px);
  width: 100%;
}

/* ── Group ───────────────────────────────────────────────────────────────── */

.panel-nav__group {
  display: flex;
  flex-direction: column;
  gap: var(--dimension-space-050, 4px);
}

/*
 * Section headers — typography via ds-text (text-caption + emphasis).
 * Element selector so display:flex beats Text's inline-block, and padding
 * isn't clobbered by sharing `.panel-nav__item-label-text` on the same host.
 */
ds-text.panel-nav__group-label {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  text-transform: uppercase;
  color: var(--_nav-fg-secondary);
  user-select: none;
  height: var(--dimension-size-300, 24px);
  /* Former nested pad: group-label (075) + item-label-text (025). */
  padding: 0 calc(var(--dimension-space-075, 6px) + var(--ds-control-label-inset));
  white-space: nowrap;
  overflow: hidden;
  max-height: var(--dimension-size-300, 24px);
  opacity: 1;
  transition:
    opacity var(--_nav-label-fade-motion) 0ms,
    max-height var(--_nav-label-fade-motion) 0ms,
    padding var(--_nav-label-fade-motion) 0ms,
    color var(--effect-motion-short-3, 200ms ease);
}

/* ── Item ────────────────────────────────────────────────────────────────── */

.panel-nav__item {
  all: unset;
  /* Re-assert after unset — required for `.ds-interaction-fill` stacking. */
  position: relative;
  z-index: 0;
  display: flex;
  align-items: center;
  gap: var(--dimension-space-050, 4px);
  padding: 0 var(--dimension-space-075, 6px);
  height: var(--dimension-size-400, 32px);
  border-radius: var(--ds-control-radius, var(--dimension-radius-025, 2px));
  cursor: default;
  color: var(--_nav-fg-secondary);
  box-sizing: border-box;
  width: 100%;
  user-select: none;
  transition: color var(--effect-motion-short-1, 50ms ease-in-out);
  /* Hover/press: `.ds-interaction-fill` — no --selected (icon/type weight only). */
}

/* ── Active item ─────────────────────────────────────────────────────────── */

.panel-nav__item--active {
  color: var(--_nav-fg-primary);
  /* Bold typography via ds-text (text-body-medium + emphasis) on the label */
}

/* ── Item icon ───────────────────────────────────────────────────────────── */

.panel-nav__item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--dimension-iconography-md, 20px);
  height: var(--dimension-iconography-md, 20px);
  position: relative;
  z-index: 2;
}

/* ── Item label ──────────────────────────────────────────────────────────── */

.panel-nav__item-label {
  position: relative;
  z-index: 2;
  flex: 1;
  min-width: 0;
  height: var(--typography-lineheight-md, 20px);
  opacity: 1;
  /* Gradient wipe: mask sweeps left-to-right on expand, right-to-left on collapse */
  /* stylelint-disable-next-line color-named -- mask luminance stops, not theme colors */
  mask-image: linear-gradient(to right, black 50%, transparent 100%);
  /* stylelint-disable-next-line color-named -- mask luminance stops, not theme colors */
  -webkit-mask-image: linear-gradient(to right, black 50%, transparent 100%);
  mask-size: 200% 100%;
  -webkit-mask-size: 200% 100%;
  mask-position: 0% 0;
  -webkit-mask-position: 0% 0;
  transition:
    opacity var(--_nav-label-fade-motion) 0ms,
    mask-position var(--_nav-label-fade-motion) 0ms,
    -webkit-mask-position var(--_nav-label-fade-motion) 0ms;
}

.panel-nav__item-label-text {
  padding: 0 var(--ds-control-label-inset);
}

/* ── Item notification dot — 20px suffix zone matches the icon geometry ──── */

.panel-nav__item-dot-box {
  position: absolute;
  z-index: 2;
  right: var(--dimension-space-075, 6px);
  top: var(--dimension-space-075, 6px);
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--dimension-iconography-md, 20px);
  height: var(--dimension-iconography-md, 20px);
  pointer-events: none;
}

.panel-nav__item-dot {
  transition:
    transform var(--_nav-expand-motion);
}

.panel-nav--collapsed .panel-nav__item-dot {
  transform: translate(
    calc(var(--dimension-space-075, 6px) + var(--dimension-stroke-width-012, 1px)),
    calc(0px - var(--dimension-space-075, 6px) - var(--dimension-stroke-width-012, 1px))
  );
}

.panel-nav--animating .panel-nav__item-label {
  text-overflow: clip;
}

.panel-nav--collapsed .panel-nav__item-label {
  opacity: 0;
  pointer-events: none;
  text-overflow: clip;
  mask-position: 100% 0;
  -webkit-mask-position: 100% 0;
  transition:
    opacity var(--_nav-label-fade-out-motion) 0ms,
    mask-position var(--_nav-label-fade-out-motion) 0ms,
    -webkit-mask-position var(--_nav-label-fade-out-motion) 0ms;
}

.panel-nav--collapsed ds-text.panel-nav__group-label {
  opacity: 0;
  max-height: 0;
  padding: 0 calc(var(--dimension-space-075, 6px) + var(--ds-control-label-inset));
  pointer-events: none;
  transition:
    opacity var(--_nav-label-fade-out-motion) 0ms,
    max-height var(--_nav-label-fade-out-motion) 0ms,
    padding var(--_nav-label-fade-out-motion) 0ms,
    color var(--effect-motion-short-3, 200ms ease);
}

/* ── Footer ──────────────────────────────────────────────────────────────── */

.panel-nav__footer {
  position: relative;
  flex-shrink: 0;
  /* single row: padding*2 + item */
  height: calc(var(--_nav-padding-y) * 2 + var(--_nav-item-height));
  transition: height var(--_nav-expand-motion);
}

.panel-nav--collapsed .panel-nav__footer {
  /* two rows: padding*2 + item*2 + gap */
  height: calc(var(--_nav-padding-y) * 2 + var(--_nav-item-height) * 2 + var(--dimension-space-050, 4px));
}

/* ── Footer icon button (gear / dashboard) ───────────────────────────────── */

.panel-nav__footer-btn {
  position: absolute;
  left: var(--_nav-padding-x);
  top: var(--_nav-padding-y);
  --ds-button-unfilled-fg: var(--_nav-fg-secondary);
  --ds-button-unfilled-fg-active: var(--_nav-fg-primary);
  --ds-interaction-hover: var(--_nav-hover);
  --ds-interaction-pressed: var(--_nav-pressed);
  --ds-focus-ring-color: var(--_nav-focus);
}

/* ── Footer user — absolutely positioned, slides from row to below gear ───── */

.panel-nav__footer-user {
  position: absolute;
  /* Expanded row: balanced shell inset with an 8px gear-to-user gap. */
  left: calc(var(--_nav-padding-x) + var(--_nav-footer-btn-w) + var(--dimension-space-100, 8px));
  right: var(--_nav-padding-x);
  top: var(--_nav-padding-y);
  width: auto;
  min-width: 0;
  transition:
    left var(--_nav-expand-motion),
    right var(--_nav-expand-motion),
    top var(--_nav-expand-motion);
}

/* Popup-open state keeps the native footer trigger visibly pressed. */
.panel-nav__footer-user--menu-expanded::after {
  background: var(--_nav-pressed);
}

.panel-nav__footer-user--menu-expanded {
  --ds-interaction-hover: var(--_nav-pressed);
}

.panel-nav--collapsed .panel-nav__footer-user {
  /* Compact column keeps the intentionally tighter 4px vertical gap. */
  left: var(--_nav-padding-x);
  top: calc(var(--_nav-padding-y) + var(--_nav-item-height) + var(--dimension-space-050, 4px));
}

/* Label: absolute so it stays in place and only fades — no mask slide */
.panel-nav__footer-user-label {
  text-align: right;
  position: absolute;
  left: var(--dimension-space-075, 6px);
  right: calc(var(--dimension-space-075, 6px) + var(--dimension-iconography-md, 20px) + var(--dimension-space-050, 4px));
  top: 50%;
  transform: translateY(-50%);
  flex: none;
  height: var(--typography-lineheight-md, 20px);
  mask-image: none;
  -webkit-mask-image: none;
  transition: opacity var(--_nav-label-fade-motion);
}

.panel-nav--collapsed .panel-nav__footer-user-label {
  opacity: 0;
  pointer-events: none;
  mask-position: 0% 0;
  -webkit-mask-position: 0% 0;
}

/* ── Footer user icon — cross-fades chevron ↔ circle+initial ────────────── */

/* Absolute — right-anchored in expanded, left-anchored in collapsed to match regular item icon position */
.panel-nav__footer-user-icon {
  position: absolute;
  left: calc(100% - var(--dimension-iconography-md, 20px) - var(--dimension-space-075, 6px));
  top: 0;
  bottom: 0;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: left var(--_nav-expand-motion);
}

.panel-nav--collapsed .panel-nav__footer-user-icon {
  left: var(--dimension-space-075, 6px);
}

.panel-nav__footer-icon-expanded,
.panel-nav__footer-icon-collapsed {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel-nav__footer-icon-expanded {
  opacity: 1;
  transition: opacity var(--_nav-label-fade-motion);
}

.panel-nav__footer-icon-collapsed {
  opacity: 0;
  transition: opacity var(--_nav-label-fade-motion);
}

.panel-nav--collapsed .panel-nav__footer-icon-expanded {
  opacity: 0;
  transition: opacity var(--_nav-label-fade-out-motion);
}

.panel-nav--collapsed .panel-nav__footer-icon-collapsed {
  opacity: 1;
  transition: opacity var(--_nav-label-fade-out-motion);
}

/* ── User initial — overlaid on the circle icon ─────────────────────────── */

/*
 * Center the caption glyph on the Circle icon. Don't use inset:0 + flex — the
 * inner `.ds-text__element { width: 100% }` stretches and left-aligns the letter.
 * Translate-centering keeps the complete caption metric box on the icon center;
 * the caption variant remains the sole owner of its line-height.
 * (Do not set `inset: auto` after top/left — it resets them.)
 */
ds-text.panel-nav__user-initial,
ds-text.panel-nav__user-initial.ds-text--caption {
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  transform: translate(-50%, -50%);
  display: block;
  width: max-content;
  max-width: none;
  min-width: 0;
  text-align: center;
  color: inherit;
  pointer-events: none;
}

/* ── Drag-to-resize handle ───────────────────────────────────────────────── */

/* The resize overlay line — extends past the right edge; keep above workspace content */
.panel-nav__resize-handle {
  position: absolute;
  top: 0;
  /* Centered on the right edge: --dimension-space-050 outside + inside = --dimension-space-100 hit zone */
  right: var(--dimension-space-n050, -4px);
  bottom: 0;
  width: var(--dimension-space-100, 8px);
  z-index: 20;
  cursor: ew-resize;
  touch-action: none;
  /* Needs a real background for Safari hit-testing */
  background: transparent;
}

/* Safari requires non-transparent fill for hit testing */
.panel-nav__resize-handle::before {
  content: '';
  position: absolute;
  inset: 0;
  /* stylelint-disable-next-line color-named -- near-invisible hit-test fill; not a theme color */
  background: rgba(0, 0, 0, 0.001);
}

/* Resize overlay line */
.panel-nav__resize-handle::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: var(--dimension-stroke-width-025, 2px);
  background: var(--color-foreground-primary);
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--effect-motion-short-2, 100ms ease-in-out);
}

.panel-nav__resize-handle--overlay::after {
  opacity: 1;
}

/* ── Reduced motion ──────────────────────────────────────────────────────── */

@media (prefers-reduced-motion: reduce) {
  .panel-nav,
  .panel-nav *,
  .panel-nav *::before,
  .panel-nav *::after,
  .panel-nav__resize-handle,
  .panel-nav__resize-handle::before,
  .panel-nav__resize-handle::after {
    /* Near-instant — browsers treat 0 as "no transition" and can skip the end state. */
    transition-duration: var(--effect-animation-duration-instant) !important;
    animation-duration: var(--effect-animation-duration-instant) !important;
    transition-delay: var(--effect-animation-duration-instant) !important;
  }
}
